import { useEffect, useState, useMemo, useCallback } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { useModal } from "../../shared/contexts";
import { useAudioContext } from "../../shared/contexts/AudioContext";
import { BetAvatar, Box, Checkbox, Typography } from "../../shared/ui";
import { useUserStore } from "../../store/userStore";
import { Icon } from "../Icon";
import styles from "./BurgerMenu.module.scss";

export interface BurgerMenuProps {
  className?: string;
}

interface MenuItem {
  id: string;
  label: string;
  onClick?: () => void;
  hasToggle?: boolean;
  isToggled?: boolean;
}

export function BurgerMenu({ className = "" }: BurgerMenuProps) {
  const { isOpenBurger, toggleBurger, openModal } = useModal();
  const { t } = useTranslation();
  const { profile } = useUserStore();
  const [currentAvatar, setCurrentAvatar] = useState("Avatar_1");

  const {
    isMusicEnabled,
    setMusicEnabled,
    isSoundEnabled,
    setSoundEnabled,
    playClickSound,
  } = useAudioContext();

  const closeMenu = useCallback(() => {
    playClickSound();
    toggleBurger();
  }, [playClickSound, toggleBurger]);

  const handleAvatarSelect = useCallback((avatar: string) => {
    setCurrentAvatar(avatar);
  }, []);

  const openAvatarSelector = useCallback(() => {
    playClickSound();
    openModal("avatarSelector", { onSelect: handleAvatarSelect });
    toggleBurger();
  }, [playClickSound, openModal, handleAvatarSelect, toggleBurger]);

  const menuItems: MenuItem[] = useMemo(
    () => [
      {
        id: "sound",
        label: t("auth.sound"),
        hasToggle: true,
        isToggled: isSoundEnabled,
        onClick: () => {
          playClickSound();
          setSoundEnabled(!isSoundEnabled);
        },
      },
      {
        id: "music",
        label: t("auth.music"),
        hasToggle: true,
        isToggled: isMusicEnabled,
        onClick: () => {
          playClickSound();
          setMusicEnabled(!isMusicEnabled);
        },
      },
      {
        id: "language",
        label: t("auth.language"),
        onClick: () => {
          playClickSound();
          openModal("language");
          toggleBurger();
        },
      },
      {
        id: "how-to-play",
        label: t("auth.how_to_play"),
        onClick: () => {
          playClickSound();
          openModal("onboarding");
          toggleBurger();
        },
      },
      {
        id: "game-rules",
        label: t("auth.game_rules"),
        onClick: () => {
          playClickSound();
          openModal("gameRules");
          toggleBurger();
        },
      },
      {
        id: "game-limits",
        label: t("auth.game_limits"),
        onClick: () => {
          playClickSound();
          openModal("gameLimits");
          toggleBurger();
        },
      },
      {
        id: "provably-fair",
        label: t("auth.provably_fair_settings"),
        onClick: () => {
          playClickSound();
          openModal("provablyFair");
          toggleBurger();
        },
      },
    ],
    [
      t,
      isSoundEnabled,
      isMusicEnabled,
      playClickSound,
      setSoundEnabled,
      setMusicEnabled,
      openModal,
      toggleBurger,
    ]
  );

  useEffect(() => {
    if (profile) {
      setCurrentAvatar(profile.avatar as string);
    }
  }, [profile]);
  return (
    <>
      <button
        className={`${styles.burgerButton} ${className}`}
        onClick={toggleBurger}
        aria-label="Open menu"
      >
        <div className={styles.burgerLines}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>

      {isOpenBurger && (
        <div className={styles.overlay} onClick={closeMenu}>
          <div className={styles.menu} onClick={(e) => e.stopPropagation()}>
            <div className={styles.menuContent}>
              <div class={styles.btn_wrapper}>
                <div className={styles.closeButton} onClick={closeMenu}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <rect width="20" height="20" rx="10" fill="#111111" />
                    <path
                      d="M9.92737 8.13056L13.8503 4.20764L15.6484 6.00579L11.7255 9.92871L15.6484 13.8516L13.8503 15.6498L9.92737 11.7269L6.00583 15.6484L4.20768 13.8503L8.12922 9.92871L4.20768 6.00717L6.00583 4.20902L9.92737 8.13056Z"
                      fill="#707071"
                    />
                  </svg>
                </div>
              </div>

              <div className={styles.profileSection}>
                <Box gap="xs" display="flex" align="center">
                  <div className={styles.profileAvatar}>
                    <BetAvatar
                      classNameAvatar={styles.profileAvatarImage}
                      avatar={`${currentAvatar}.png`}
                      size={36}
                    />
                  </div>
                  <Typography variant="tab-select" color="header">
                    {profile?.nickname}
                  </Typography>
                </Box>
                <div className={styles.profileInfo}>
                  <button
                    className={styles.changeAvatarButton}
                    onClick={openAvatarSelector}
                  >
                    <Typography
                      className={styles.text}
                      variant="caption2"
                      color="light-grey"
                    >
                      {t("auth.change_avatar")}
                    </Typography>
                  </button>
                </div>
              </div>

              <div className={styles.menuItems}>
                {menuItems.map((item) => {
                  const content = (
                    <>
                      <div className={styles.menuItemContent}>
                        <div className={styles.menuItemIcon}>
                          <Icon
                            name={item.id}
                            width={20}
                            height={20}
                            color={item.isToggled ? "#5AC8FA" : "#707071"}
                          />
                        </div>
                        <span className={styles.menuItemLabel}>
                          {item.label}
                        </span>
                      </div>

                      {item.hasToggle && (
                        <Checkbox
                          checked={item.isToggled}
                          onChange={() => item.onClick?.()}
                          size="md"
                        />
                      )}
                    </>
                  );

                  return (
                    <div
                      key={item.id}
                      className={styles.menuItem}
                      onClick={item.onClick}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
